// SinglePickerField provides a wrapper to support
// existing standalone SinglePicker instances in reportbuilder
// SinglePickerField receives value as a string.
//
// Before using this component, I recommend reviewing OptionPickerField
// since it supports a variety of single and multi-value use cases
// with props and sends/receives value as an object.
// OptionPickerField may be a better choice depending on your use case.
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { kebabCase } from 'lodash';
import { conformanceSelector, enabledSelector, labelSelector, newPathSelector, optionsSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import ConformanceTooltip from './conformanceTooltip';
import Label from './label';
import SinglePicker from '../../common/controls/singlePicker';
import logValueOnChange from '../commonActions/logValueOnChange';
import { leafPedigreesString } from '../../../../behaviors/src/lib/common';
import { removeNulls } from '../../../utils/contentTools';
const SinglePickerField = ({
  allowsOther: _allowsOther = false,
  className: _className = '',
  conformanceText: _conformanceText = null,
  enabled,
  endMessage: _endMessage = null,
  dropdownIndicatorGlyph: _dropdownIndicatorGlyph = 'chevron-down',
  label: _label = null,
  labelKey: _labelKey = 'title',
  onChange,
  onFocus: _onFocus = () => null,
  options: _options = [],
  placeholder: _placeholder = '',
  value: _value = '',
  valueKey: _valueKey = 'identifier',
  otherKey: _otherKey = null,
  path,
  onTextChange: _onTextChange = null,
  isLoading: _isLoading = false,
  isSearching: _isSearching = false
}) => {
  const showConformance = !!_conformanceText;
  const otherValueKey = _otherKey || _valueKey;

  /**
   * @method handleChange - if the new value is an "Other", set the `otherValueKey` ("identifier")
   * as a UUID
   */
  const handleChange = newValue => {
    const result = JSON.parse(JSON.stringify(newValue));
    if (_allowsOther && result && result[_valueKey]) {
      const optionsMatchingNewValue = _options.filter(opt => opt[_valueKey] === result[_valueKey]);
      if (optionsMatchingNewValue.length === 0) {
        delete result[_valueKey];
        result[otherValueKey] = uuid();
      }
    }
    const displayValue = leafPedigreesString(removeNulls(result));
    onChange({
      path,
      value: result,
      displayValue
    });
  };

  /**
   * @method prepareOptions - to satisfy the root `Select` component in `SinglePicker`, the value
   * prop must also be in the list of available options.
   */
  const prepareOptions = () => {
    let result = JSON.parse(JSON.stringify(_options));
    if (_allowsOther && _value && _value[_valueKey]) {
      const optionsMatchingValue = _options.filter(opt => opt[_valueKey] === _value[_valueKey]);
      if (optionsMatchingValue.length === 0) {
        result = result.concat(_value);
      }
    }
    return result;
  };
  const id = useMemo(() => `t-spf-${kebabCase(path)}`, [path]);
  let pickerClassName = `${_className} t-spf-${kebabCase(_label || path)}`;
  if (showConformance) {
    pickerClassName += ' is-invalid';
  }
  return /*#__PURE__*/React.createElement("div", {
    id: id
  }, /*#__PURE__*/React.createElement(Label, {
    value: _label
  }), /*#__PURE__*/React.createElement(SinglePicker, {
    allowCreate: _allowsOther,
    disabled: !enabled,
    dropdownIndicatorGlyph: _dropdownIndicatorGlyph,
    className: pickerClassName,
    labelKey: _labelKey,
    onFocus: _onFocus,
    onChange: handleChange,
    options: prepareOptions(),
    placeholder: _placeholder,
    endMessage: _endMessage,
    value: _value,
    valueKey: _valueKey,
    verticalPadding: false,
    onTextChange: _onTextChange,
    isLoading: _isLoading,
    isSearching: _isSearching,
    path: path
  }), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    errorText: _conformanceText,
    target: id,
    placement: "right"
  }));
};
SinglePickerField.propTypes = {
  allowsOther: PropTypes.bool,
  className: PropTypes.string,
  conformanceText: PropTypes.string,
  endMessage: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  path: PropTypes.string.isRequired,
  dropdownIndicatorGlyph: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  otherKey: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.string,
  isLoading: PropTypes.bool,
  isSearching: PropTypes.bool,
  onTextChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.shape({})]),
  valueKey: PropTypes.string
};
export { SinglePickerField as UncomposedSinglePickerField };
export default entityComposition(SinglePickerField, [], {
  conformanceText: conformanceSelector,
  enabled: enabledSelector,
  label: labelSelector,
  options: optionsSelector,
  value: valueSelector,
  onChange: logValueOnChange,
  path: newPathSelector
});